import React, { useState, useEffect, useCallback } from 'react';
import { collection, updateDoc, setDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { createUserAsAdmin } from '../utils/createUser';
import { schoolClassSortOrder } from '../services/TimetableService';
import { adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';
import './AdminTeachersView.css';
import './AdminDetailPages.css';
import StatCards from './StatCards';

function AdminTeachersView({ adminData }) {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [newTeacher, setNewTeacher] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    subjects: [],
    teacherType: 'regular'
  });

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadTeachers = useCallback(async () => {
    console.log('🔥 AdminTeachers: Loading teachers, schoolId:', adminData?.schoolId);
    try {
      let usersData = await dataCacheService.getDocs(ck('users'), collection(db, 'users'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        usersData = sid ? usersData.filter(u => (u.schoolId || '').toLowerCase() === sid) : [];
      }
      // Show ALL teachers, no type filtering
      const teachersData = usersData.filter(user => user.role === 'Teacher');
      console.log('🔥 AdminTeachers: Teachers loaded', teachersData.length, teachersData);
      setTeachers(teachersData);
    } catch (error) {
      console.error('🔥 AdminTeachers: Error loading teachers:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminTeachers: Loading classes, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        classesData = sid ? classesData.filter(c => (c.schoolId || '').toLowerCase() === sid) : [];
      }
      console.log('🔥 AdminTeachers: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminTeachers: Error loading classes:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadTeachers();
    loadClasses();
  }, [loadTeachers, loadClasses]);

  const getFilteredTeachers = () => {
    if (!searchText) {
      return teachers;
    }
    const searchLower = searchText.toLowerCase();
    return teachers.filter(teacher =>
      teacher.firstName?.toLowerCase().includes(searchLower) ||
      teacher.lastName?.toLowerCase().includes(searchLower) ||
      teacher.email?.toLowerCase().includes(searchLower) ||
      teacher.username?.toLowerCase().includes(searchLower)
    );
  };

  const getTeacherClasses = (teacher) => {
    return classes.filter(cls =>
      cls.teacherId === teacher.id ||
      cls.teacherId === teacher.uid ||
      (teacher.assignedClassIds || []).includes(cls.id)
    );
  };

  const getTeacherName = (teacher) => {
    const full = `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim();
    return full || teacher.username || teacher.email || 'Unknown Teacher';
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    if (!newTeacher.password || newTeacher.password.length < 6) {
      alert('Password is required and must be at least 6 characters.');
      return;
    }
    try {
      // Create a real Firebase Auth account via a secondary app so the admin
      // is not signed out. Without this the teacher could never log in.
      const uid = await createUserAsAdmin(newTeacher.email, newTeacher.password);
      const username = newTeacher.username || `${newTeacher.firstName} ${newTeacher.lastName}`.trim();
      const userData = {
        id: uid,
        uuid: crypto.randomUUID(),
        username,
        firstName: newTeacher.firstName,
        lastName: newTeacher.lastName,
        email: newTeacher.email,
        phoneNumber: newTeacher.phoneNumber || '',
        role: 'Teacher',
        teacherType: newTeacher.teacherType,
        specialtySubjects: newTeacher.teacherType === 'subject' ? (newTeacher.subjects || []) : [],
        assignedClassIds: [],
        schoolId: adminData?.schoolId || null,
        createdAt: new Date().toISOString(),
        isActive: true
      };
      await setDoc(doc(db, 'users', uid), userData);
      await setDoc(doc(db, 'teachers', uid), {
        id: uid,
        email: newTeacher.email,
        fullName: username,
        teacherType: newTeacher.teacherType,
        specialtySubjects: newTeacher.teacherType === 'subject' ? (newTeacher.subjects || []) : [],
        createdAt: new Date().toISOString(),
        isActive: true,
        schoolId: adminData?.schoolId || null
      });
      dataCacheService.invalidatePath('users');
      dataCacheService.invalidatePath('teachers');
      setShowAddForm(false);
      setNewTeacher({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        username: '',
        password: '',
        subjects: [],
        teacherType: 'regular'
      });
      loadTeachers();
    } catch (error) {
      console.error('Error adding teacher:', error);
      alert(`Failed to add teacher: ${error.message}`);
    }
  };

  const handleEditTeacher = (teacher) => {
    setIsEditing(true);
    setEditTeacher(teacher);
    setNewTeacher({
      firstName: teacher.firstName || '',
      lastName: teacher.lastName || '',
      email: teacher.email || '',
      phoneNumber: teacher.phoneNumber || '',
      username: teacher.username || '',
      subjects: teacher.specialtySubjects || [],
      teacherType: teacher.teacherType || 'regular'
    });
    setShowAddForm(true);
  };

  const handleUpdateTeacher = async (e) => {
    e.preventDefault();
    try {
      // Only update editable fields — username and email stay as-is from editTeacher
      await updateDoc(doc(db, 'users', editTeacher.id), {
        firstName: newTeacher.firstName,
        lastName: newTeacher.lastName,
        phoneNumber: newTeacher.phoneNumber,
        specialtySubjects: newTeacher.subjects,
        teacherType: newTeacher.teacherType,
        updatedAt: new Date().toISOString()
      });
      dataCacheService.invalidatePath('users');
      setShowAddForm(false);
      setIsEditing(false);
      setEditTeacher(null);
      setNewTeacher({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        username: '',
        subjects: [],
        teacherType: 'regular'
      });
      loadTeachers();
    } catch (error) {
      console.error('Error updating teacher:', error);
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        await updateDoc(doc(db, 'users', teacherId), {
          isActive: false,
          deletedAt: new Date().toISOString()
        });
        dataCacheService.invalidatePath('users');
        loadTeachers();
        return true;
      } catch (error) {
        console.error('Error deleting teacher:', error);
      }
    }
    return false;
  };

  const handleSubjectToggle = (subject) => {
    setNewTeacher(prev => {
      const subjects = prev.subjects || [];
      if (subjects.includes(subject)) {
        return { ...prev, subjects: subjects.filter(s => s !== subject) };
      } else {
        return { ...prev, subjects: [...subjects, subject] };
      }
    });
  };

  const resetForm = () => {
    setShowAddForm(false);
    setIsEditing(false);
    setEditTeacher(null);
    setNewTeacher({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      username: '',
      subjects: [],
      teacherType: 'regular'
    });
  };

  if (loading) {
    return <div className="loading">Loading teachers...</div>;
  }

  // Add/Edit modal — shared by the list page and the detail page
  const teacherFormModal = showAddForm && (
    <div className="modal-overlay" onClick={resetForm}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isEditing ? 'Edit Teacher' : 'Add Teacher'}</h3>
        <form onSubmit={isEditing ? handleUpdateTeacher : handleAddTeacher}>
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={newTeacher.firstName}
              onChange={(e) => setNewTeacher({ ...newTeacher, firstName: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              value={newTeacher.lastName}
              onChange={(e) => setNewTeacher({ ...newTeacher, lastName: e.target.value })}
              required
            />
          </div>
          {!isEditing && (
            <>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={newTeacher.username}
                  onChange={(e) => setNewTeacher({ ...newTeacher, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={newTeacher.password || ''}
                  onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
                  minLength={6}
                  required
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={newTeacher.phoneNumber}
              onChange={(e) => setNewTeacher({ ...newTeacher, phoneNumber: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Teacher Type</label>
            <select
              value={newTeacher.teacherType}
              onChange={(e) => setNewTeacher({ ...newTeacher, teacherType: e.target.value })}
            >
              <option value="regular">Class Teacher</option>
              <option value="subject">Subject Teacher</option>
            </select>
          </div>
          <div className="form-group">
            <label>Specialty Subjects</label>
            <div className="subjects-grid">
              {availableSubjects.map(subject => (
                <label key={subject} className="subject-checkbox">
                  <input
                    type="checkbox"
                    checked={newTeacher.subjects?.includes(subject) || false}
                    onChange={() => handleSubjectToggle(subject)}
                  />
                  <span>{subject}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button type="button" onClick={resetForm}>Cancel</button>
            <button type="submit">{isEditing ? 'Update' : 'Add'}</button>
          </div>
        </form>
      </div>
    </div>
  );

  // Full-screen teacher detail page — replaces the list
  if (selectedTeacher) {
    const teacher = teachers.find(t => t.id === selectedTeacher.id) || selectedTeacher;
    const teacherClasses = getTeacherClasses(teacher);
    const teacherName = getTeacherName(teacher);
    const isActive = teacher.isActive !== false;
    return (
      <div className="admin-teachers-view">
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setSelectedTeacher(null)}>
            ◄ Back to Teachers
          </button>

          <div className="detail-hero">
            <div className="detail-avatar">
              {teacher.photoURL
                ? <img src={teacher.photoURL} alt={teacherName} />
                : (teacher.firstName ? teacher.firstName[0].toUpperCase() : (teacher.username ? teacher.username[0].toUpperCase() : 'T'))}
            </div>
            <div className="detail-hero-info">
              <h2>{teacherName}</h2>
              <p className="detail-subtitle">{teacher.email || 'No email'}</p>
              <div className="detail-badges">
                <span className="detail-badge">{teacher.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</span>
                <span className={`detail-badge ${isActive ? 'green' : 'red'}`}>{isActive ? 'Active' : 'Inactive'}</span>
                {teacherClasses.length > 0 && (
                  <span className="detail-badge gray">{teacherClasses.length} {teacherClasses.length === 1 ? 'class' : 'classes'}</span>
                )}
              </div>
            </div>
            <div className="detail-hero-actions">
              <button className="detail-btn detail-btn-edit" onClick={() => handleEditTeacher(teacher)}>
                Edit
              </button>
              <button
                className="detail-btn detail-btn-delete"
                onClick={async () => { if (await handleDeleteTeacher(teacher.id)) setSelectedTeacher(null); }}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-card">
              <h3>Contact &amp; Account</h3>
              <div className="detail-field">
                <span className="field-label">Email</span>
                <span className="field-value">{teacher.email || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Username</span>
                <span className="field-value">{teacher.username || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Phone</span>
                <span className="field-value">{teacher.phoneNumber || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Status</span>
                <span className="field-value">{isActive ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Created</span>
                <span className="field-value">{teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">User ID</span>
                <span className="field-value">{teacher.id}</span>
              </div>
            </div>

            <div className="detail-card">
              <h3>Specialty Subjects</h3>
              {(teacher.specialtySubjects || []).length === 0 ? (
                <p className="detail-empty">No specialty subjects</p>
              ) : (
                <div className="detail-chips">
                  {teacher.specialtySubjects.map(subject => (
                    <span key={subject} className="detail-chip">{subject}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="detail-card">
              <h3>Assigned Classes ({teacherClasses.length})</h3>
              {teacherClasses.length === 0 ? (
                <p className="detail-empty">No classes assigned</p>
              ) : (
                <div className="detail-list">
                  {teacherClasses.map(cls => (
                    <div key={cls.id} className="detail-list-item">
                      <span className="item-name">{cls.name || 'Unnamed Class'}</span>
                      <span className="item-meta">{cls.grade || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {teacherFormModal}
      </div>
    );
  }

  return (
    <div className="admin-teachers-view">
      <div className="header">
        <h2>Teacher Management</h2>
        <button className="add-button" onClick={() => { resetForm(); setShowAddForm(true); }}>
          + Add Teacher
        </button>
      </div>
      <StatCards items={[['Teachers',teachers.length],['Home Teachers',teachers.filter(t => t.teacherType !== 'subject').length],['Subject Teachers',teachers.filter(t => t.teacherType === 'subject').length],['Unassigned',teachers.filter(t => t.teacherType !== 'subject' && !classes.some(c => c.teacherId === t.id || c.teacherId === t.uid)).length],['Classes',classes.length]]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search teachers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <div className="teachers-list">
        {getFilteredTeachers().length === 0 ? (
          <p className="no-results">No teachers found</p>
        ) : (
          getFilteredTeachers().map(teacher => {
            const teacherClasses = getTeacherClasses(teacher);
            return (
              <div key={teacher.id} className="teacher-card" onClick={() => setSelectedTeacher(teacher)}>
                <div className="teacher-avatar">
                  {teacher.firstName ? teacher.firstName[0].toUpperCase() : (teacher.username ? teacher.username[0].toUpperCase() : 'T')}
                </div>
                <div className="teacher-info">
                  <h3>{teacher.firstName || ''} {teacher.lastName || ''}</h3>
                  <p className="teacher-email">{teacher.email || 'No email'}</p>
                  <p className="teacher-meta">
                    <span className="teacher-type-badge">{teacher.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</span>
                    {teacherClasses.length > 0 && <span className="teacher-classes">{teacherClasses.length} classes</span>}
                  </p>
                </div>
                <div className="teacher-actions">
                  <button className="edit-button" onClick={(e) => { e.stopPropagation(); handleEditTeacher(teacher); }}>
                    Edit
                  </button>
                  <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteTeacher(teacher.id); }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {teacherFormModal}
    </div>
  );
}

export default AdminTeachersView;
